import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Builder_, Checkbox_, Dropdown_, Grey_, Main_, PageFull, PageTitle, Partial_Loader, device } from "monica-alexandria";
import { buildSchemaFromLanding } from "../crm/utils";
import l from "../../services/api/Landings";
import b from "../../services/api/Boards";
import { useCRMContext } from "../../context/CrmContext";
import { assertCrmExportReady } from '../crm/crmExportStatus';
import { buildCrmExportFields, buildCrmExportRow, getCrmExportFieldId, saveCrmExport } from "../crm/crmExport";
const LEAD_CUSTOM_FIELDS = '__lead_custom_fields__';


const PageWrap = styled(PageFull)`
  display: flex;
  flex-direction: column;
  /* gap: 1rem; */
  padding-bottom: 4rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  /* padding: var(--normalPads); */

  @media ${device.sm} {
    padding: 2rem;
  }
`;

const Card = styled.div`
  background: ${(p) => p.theme.mid};
  border: 1px solid ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  padding: var(--normalPads);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BoardList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media ${device.md} {
    grid-template-columns: 1fr;
  }
`;

const BoardItem = styled.div`
  background: ${(p) => p.theme.background};
  border: 1px solid ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  padding: var(--smallPads);
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;

  @media ${device.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media ${device.sm} {
    grid-template-columns: 1fr;
  }
`;

const FieldItem = styled.div``;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

function getQuestLabel(quest) {
  return (
    quest?.name ||
    `${quest?.questionnaires?.[0]?.answer || ""} ${quest?.questionnaires?.[1]?.answer || ""}`
  );
}

async function readBoardSchema(quest) {
  const response = quest?.isSidequest
    ? { data: { landing: { defaultFormFields: ['FullName', 'Tel', 'Email', 'City'] } } }
    : await l.getLandingById(quest?._id);
  assertCrmExportReady(response, 'landing');
  return buildSchemaFromLanding(response.data.landing);
}

async function getAllColumnLeads(questId, columnId) {
  const items = [];
  let skip = 0;
  const limit = 100;
  let hasMore = true;

  while (hasMore) {
    const res = await b.getColumnLeads(questId, columnId, skip, limit, "");
    assertCrmExportReady(res, 'items');
    const batch = res?.data?.items || [];

    items.push(...batch);
    hasMore = !!res?.data?.hasMore;
    skip += limit;

    if (!batch.length) break;
  }

  return items;
}

export default function CRM_EXPORT() {
  const { t } = useTranslation();
  const { quests, archivedQuests, loading } = useCRMContext();

  const allQuests = useMemo(() => {
    return [...(quests || []), ...(archivedQuests || [])];
  }, [quests, archivedQuests]);

  const [selectedBoards, setSelectedBoards] = useState(new Set());
  const [selectAllBoards, setSelectAllBoards] = useState(false);
  const [allFieldsByBoard, setAllFieldsByBoard] = useState({});
  const [selectedFieldsByBoard, setSelectedFieldsByBoard] = useState({});
  const [selectAllFieldsByBoard, setSelectAllFieldsByBoard] = useState({});
  const [fileType, setFileType] = useState("xlsx");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [checkingBoards, setCheckingBoards] = useState(false);
  const [questIdsWithBoards, setQuestIdsWithBoards] = useState(new Set());

  useEffect(() => {
    let mounted = true;
    if (!allQuests.length) {
      setQuestIdsWithBoards(new Set());
      return;
    }

    const checkBoards = async () => {
      setCheckingBoards(true);
      const results = await Promise.all(
        allQuests.map(async (quest) => {
          const res = await b.getBoard(quest?._id);
          const cols = res?.data?.board?.columns;
          const hasBoard = !!cols && Object.keys(cols).length > 0;
          return { id: quest?._id, hasBoard };
        })
      );

      if (!mounted) return;
      const next = new Set(results.filter(r => r.hasBoard).map(r => r.id));
      setQuestIdsWithBoards(next);
      setCheckingBoards(false);

      setSelectedBoards((prev) => {
        const filtered = new Set(Array.from(prev).filter(id => next.has(id)));
        return filtered;
      });
    };

    checkBoards();
    return () => { mounted = false; };
  }, [allQuests]);

  const loadFieldChoices = async quest => {
    const schema = await readBoardSchema(quest);
    const fields = buildCrmExportFields({ formSchema: schema, t })
      .filter(field => field.key !== 'Column').map(getCrmExportFieldId);
    if (quest.isStandaloneBoard) fields.push(LEAD_CUSTOM_FIELDS);
    return fields;
  };

  const toggleBoard = async questId => {
    setExportError('');
    const next = new Set(selectedBoards);
    if (next.has(questId)) next.delete(questId);
    else {
      try {
        if (!allFieldsByBoard[questId]) {
          const quest = allQuests.find(board => String(board._id) === String(questId));
          const fields = await loadFieldChoices(quest);
          setAllFieldsByBoard(previous => ({ ...previous, [questId]: fields }));
          setSelectedFieldsByBoard(previous => ({ ...previous, [questId]: fields }));
        }
        next.add(questId);
      } catch (error) { setExportError(t(error.message));return; }
    }
    setSelectedBoards(next);
    setSelectAllBoards(next.size === questIdsWithBoards.size);
  };

  const toggleSelectAllBoards = async () => {
    setExportError('');
    if (selectAllBoards) {
      setSelectedBoards(new Set());setSelectAllBoards(false);return;
    }
    try {
      const choices = await Promise.all(allQuests
        .filter(quest => questIdsWithBoards.has(quest._id) && !allFieldsByBoard[quest._id])
        .map(async quest => [quest._id, await loadFieldChoices(quest)]));
      const fields = Object.fromEntries(choices);
      setAllFieldsByBoard(previous => ({ ...previous, ...fields }));
      setSelectedFieldsByBoard(previous => ({ ...previous, ...fields }));
      setSelectedBoards(new Set(questIdsWithBoards));setSelectAllBoards(true);
    } catch (error) { setExportError(t(error.message)); }
  };

  const toggleField = (questId, field) => {
    const current = new Set(selectedFieldsByBoard[questId] || []);
    if (current.has(field)) {
      current.delete(field);
    } else {
      current.add(field);
    }

    setSelectedFieldsByBoard((prev) => ({
      ...prev,
      [questId]: Array.from(current),
    }));
  };

  const toggleSelectAllFields = (questId, allFields) => {
    const next = !selectAllFieldsByBoard[questId];
    setSelectAllFieldsByBoard((prev) => ({ ...prev, [questId]: next }));
    setSelectedFieldsByBoard((prev) => ({
      ...prev,
      [questId]: next ? [...allFields] : [],
    }));
  };

  const handleExport = async () => {
    if (!selectedBoards.size) return;

    const boardsPayload = Array.from(selectedBoards).map((questId) => ({
      questId,
      fields: selectedFieldsByBoard[questId] || [],
    }));

    if (boardsPayload.some((b) => !b.fields.length)) return;

    setExporting(true);
    setExportError('');

    try {
      const dataByBoard = await Promise.all(
        boardsPayload.map(async ({ questId, fields }) => {
          const quest = allQuests.find((q) => String(q._id) === String(questId));
          const schema = await readBoardSchema(quest || { _id: questId });
          const boardRes = await b.getBoard(questId);
          assertCrmExportReady(boardRes, 'board');
          const boardColumns = boardRes?.data?.board?.columns || {};
          const columnIds = Object.keys(boardColumns);

          const leadsByColumn = await Promise.all(
            columnIds.map(async (columnId) => {
              const column = boardColumns[columnId];
              const leads = await getAllColumnLeads(questId, columnId);
              return { column, leads };
            })
          );
          const exportFields = buildCrmExportFields({ formSchema: schema, leads: leadsByColumn.flatMap(group => group.leads), t }).filter(
            field => field.key === 'Column' || fields.includes(getCrmExportFieldId(field)) ||
              (quest?.isStandaloneBoard && fields.includes(LEAD_CUSTOM_FIELDS) && field.isCustom)
          );
          const rows = leadsByColumn.flatMap(({ column, leads }) => leads.map(lead =>
            buildCrmExportRow(lead, exportFields, { columnTitle: column?.title || '', t })
          ));

          return {
            name: getQuestLabel(quest),
            rows,
          };
        })
      );

      saveCrmExport(dataByBoard.flatMap(board => board.rows), { fileType });
    } catch (error) {
      setExportError(t(error.message || 'Could not export contacts. Please try again.'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageWrap>
      <PageTitle>
        <h1>{t("Export data")}</h1>
      </PageTitle>

      {(loading || checkingBoards) && <Partial_Loader />}
      {exportError ? <p role="alert">{exportError}</p> : null}

      {!loading && (
        <Section>
          <Card>
            <h4>{t("Select boards")}</h4>
            <FieldItem>
              <Checkbox_
                type="checkbox"
                checked={selectAllBoards}
                onClick={toggleSelectAllBoards}
                text={t("Select all boards")}
              />
            </FieldItem>
            <BoardList>
              {allQuests
                .filter((q) => questIdsWithBoards.has(q._id))
                .map((quest) => (
                  <BoardItem key={quest._id}>
                    <Checkbox_
                      type="checkbox"
                      checked={selectedBoards.has(quest._id)}
                      onClick={() => toggleBoard(quest._id)}
                      text={getQuestLabel(quest)}
                    />
                  </BoardItem>
                ))}
            </BoardList>
          </Card>

          {Array.from(selectedBoards).map((questId) => {
            const quest = allQuests.find((q) => q._id === questId);
            const fields = selectedFieldsByBoard[questId] || [];
            const allFields = allFieldsByBoard[questId] || [];
            return (
              <Card key={questId}>
                <h4>{t("Fields for")} {getQuestLabel(quest)}</h4>
                <FieldItem>
                  <Checkbox_
                    type="checkbox"
                    checked={!!selectAllFieldsByBoard[questId]}
                    onChange={() => toggleSelectAllFields(questId, allFields)}
                    text={t("Select all fields")}
                  />
                </FieldItem>
                <FieldGrid>
                  {allFields.map((field) => (
                    <FieldItem key={field}>
                      <Checkbox_
                        type="checkbox"
                        text={field === LEAD_CUSTOM_FIELDS ? t('Custom fields') : field.startsWith("custom:") ? field.replace("custom:", "") : t(field)}
                        checked={fields.includes(field)}
                        onChange={() => toggleField(questId, field)}
                      />
                    </FieldItem>
                  ))}
                </FieldGrid>
              </Card>
            );
          })}

          <Card>
            <h4>{t("File type")}</h4>
            <Builder_>
              <Dropdown_ label={t("Export format")}>
                <select value={fileType} onChange={(e) => setFileType(e.target.value)}>
                  <option value="xls">XLS</option>
                  <option value="xlsx">XLSX</option>
                  <option value="csv">CSV</option>
                </select>
              </Dropdown_>
            </Builder_>
            <Actions>
              <Main_ text={t("Export")} onClick={handleExport} />
              {exporting && <Grey_ text={t("Preparing file...")} noPadding />}
            </Actions>
          </Card>
        </Section>
      )}
    </PageWrap>
  );
}
